import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "pest-control-web-design-colorado",
  metaTitle: "Pest Control Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom pest control web design across Colorado — fast, mobile-first, conversion-tested sites built to convert local searches into booked jobs. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Pest control marketing", to: "/pest-control-marketing-colorado" },
  ],
  currentLabel: "Pest Control Web Design Colorado",
  eyebrow: "Pest Control Web Design — Colorado",
  h1: (
    <>
      Pest Control <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded pest control websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Pest Control websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first pest control sites with sticky click-to-call, dedicated service pages, and conversion-tested booking flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Pest Control web design in Colorado",
    body: "Pest Control web design is the practice of building conversion-focused, mobile-first websites for pest control companies — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load — \"exterminator near me\" is a now-search." },
    { title: "Service pages per pest", body: "Ants, spiders, mice, bed bugs, wasps, termites, mosquitoes — each ranks independently." },
    { title: "Same-day service messaging", body: "Same-day or next-day availability surfaced above the fold." },
    { title: "Booking + recurring plan signup", body: "Integration with PestPac, FieldRoutes, or your CRM for real-time booking." },
    { title: "Trust + certification badges", body: "State licensing, NPMA, QualityPro badges where homeowners actually look." },
    { title: "Schema and SEO baked in", body: "LocalBusiness, Service, FAQPage, Review schema at launch." },
  ],
  whyThisMarket: [
    { heading: "Pest-specific pages outrank generic ones", body: "\"Bed bug exterminator\" and \"wasp removal\" have different intent, different price, different page." },
    { heading: "Recurring plans are the LTV play", body: "Quarterly recurring plan signup forms above the fold drive 3x customer lifetime value." },
    { heading: "Most pest sites are slow templates", body: "Industry-template sites lose the Map Pack click. Hand-coded wins by default." },
  ],
  relatedLinks: [
    { to: "/pest-control-marketing-colorado", label: "Pest control marketing in Colorado — full hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado pest control case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a pest control website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a pest control website?", a: "Typical timeline is 4–6 weeks for a 12–20 page pest control site with full service and service-area pages. Larger multi-location pest control companies can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs pest control companies?", a: "No — we build pest control sites for companies across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Pest Control Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for pest control companies across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/pest-control-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
